import { FC, useCallback, useContext, useEffect } from "react";

import { DesignTokenProps, createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import cn from "classnames";

import { Button, CloseButtonAppearance } from "../Button";
import * as Color from "../Color";
import { Icon, IconName } from "../Icon";
import { AlertContext } from "./AlertContext";
import { AlertType } from "./AlertType";
import { AlertVariant } from "./AlertVariant";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/**
 * The alert component props
 */
export interface AlertProps extends DesignTokenProps {
  /**
   * The alert id.
   */
  readonly id?: string;

  /**
   * The optional test ID.
   */
  readonly testId?: string;

  /**
   * The alert CSS class name.
   */
  readonly className?: string;

  /**
   * The alert message
   */
  readonly message?: string | Error;

  /**
   * The type of the alert component.
   */
  readonly type?: AlertType;

  /**
   * The variant of the alert component.
   */
  readonly variant?: AlertVariant;

  /**
   * The style of the alert component.
   * @deprecated Use variant instead.
   */
  readonly style?: AlertVariant;

  /** Indicates whether the alert is on the root layer. */
  readonly root: boolean;

  /**
   * Whether the alert is critical and should be announced immediately
   */
  readonly critical?: boolean;
}

/**
 * The mapping of alert types to icons.
 */
type AlertIcons = Record<AlertType, IconName>;

const useResolvedAlertVariants = createComponentStyleHook<AlertProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "alert",
});

/**
 * The alert component
 */
export const Alert: FC<AlertProps> = (props: AlertProps): JSX.Element => {
  const {
    testId,
    className: auxClassName,
    type = "success",
    variant: variantProp,
    style: styleProp,
    message,
    root,
    critical = false,
  } = props;
  const variant = variantProp ?? styleProp ?? "banner";
  const { mode, styles, StyleModeBoundary } = useResolvedAlertVariants(props);

  const { hideAlert } = useContext(AlertContext);

  const handleRemoveClick = useCallback(() => {
    hideAlert();
  }, [hideAlert]);

  useEffect(() => {
    const isValidType = message instanceof Error || typeof message === "string";
    if (!isValidType) {
      console.warn("An attempt was made to pass invalid data into an alert message.");
      console.warn("Only Error instances and strings are supported, but got", message);
    }
  }, [message]);

  const alertClasses = cn("alert", styles.Alert, styles[type], styles[variant], auxClassName);

  const alertIcons: AlertIcons = {
    success: "check-circle",
    warning: "warning-circle",
    error: "x-circle",
    informational: "info-circle",
  };

  const removeIcon = (
    // TODO: add key handlers to buttons
    <Button
      icon="x"
      appearance={CloseButtonAppearance}
      aria-label="Close alert"
      tabIndex={0}
      onClick={handleRemoveClick}
      className={styles.removeIcon}
    />
  );

  let messageText = "An unknown error occurred.";
  if (message instanceof Error) {
    messageText = message.message;
  } else if (typeof message === "string") {
    messageText = message;
  }
  // TODO: revisit focus management for alerts
  return (
    <StyleModeBoundary mode={mode}>
      <div
        role="alert"
        aria-live={critical ? "assertive" : "polite"}
        data-testid={testId && root ? `test__${testId}` : undefined}
        className={alertClasses}
      >
        {variant === "banner" && <div role="presentation" className={styles.pseudo} />}
        <div className={styles.messageContainer}>
          <Icon aria-label={`${type} icon`} className={styles.alertIcon} name={alertIcons[type]} color={Color.White} size="regular" />
          <span className="message">{messageText}</span>
        </div>
        {variant === "banner" && removeIcon}
      </div>
    </StyleModeBoundary>
  );
};
